/**
 * ResourceUtilizationChart — CPU/RAM/Storage donut charts
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import type { HostResources } from '@/services/api/infrastructureApi';
import { formatMemory, formatStorage } from '@/services/api/infrastructureApi';

interface Props {
  resources: HostResources;
}

interface GaugeProps {
  label: string;
  used: number;
  total: number;
  format: (v: number) => string;
  color: string;
}

const COLORS = {
  used: 'hsl(var(--primary))',
  free: 'hsl(var(--muted))',
};

const Gauge: React.FC<GaugeProps> = ({ label, used, total, format, color }) => {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const data = [
    { name: 'used', value: used },
    { name: 'free', value: Math.max(0, total - used) },
  ];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              innerRadius={28}
              outerRadius={40}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
            >
              <Cell fill={color} />
              <Cell fill={COLORS.free} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-semibold">{pct}%</span>
        </div>
      </div>
      <p className="text-xs font-medium mt-1">{label}</p>
      <p className="text-xs text-muted-foreground">
        {format(used)} / {format(total)}
      </p>
    </div>
  );
};

export const ResourceUtilizationChart: React.FC<Props> = ({ resources }) => {
  const totalStorageGB = resources.storageRepositories.reduce((sum, sr) => sum + sr.totalSizeGB, 0);
  const usedStorageGB = resources.storageRepositories.reduce((sum, sr) => sum + sr.usedSizeGB, 0);

  return (
    <div className="flex items-center justify-around py-2">
      <Gauge
        label="CPU"
        used={resources.cpuUsed}
        total={resources.cpuTotal}
        format={(v) => `${v} cores`}
        color="#7C3AED"
      />
      <Gauge
        label="Memory"
        used={resources.memoryUsedMB}
        total={resources.memoryTotalMB}
        format={formatMemory}
        color="#059669"
      />
      <Gauge
        label="Storage"
        used={usedStorageGB}
        total={totalStorageGB}
        format={formatStorage}
        color="#0EA5E9"
      />
    </div>
  );
};
